import { useState } from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Indicator,
  Loader,
  Popover,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { IconBell } from '@tabler/icons-react';
import { useTranslation } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import type { NotificationRecord } from '@billing/validation';
import {
  useListNotifications,
  useMarkAllNotificationsRead,
  useSetNotificationRead,
} from 'api/resource/notification';
import { NotificationListItem } from './NotificationListItem';
import { ReportPreviewModal } from './ReportPreviewModal';
import {
  formatUnreadCount,
  getUnreadIndicatorColor,
  UNREAD_INDICATOR_SIZE,
} from 'styles/UnreadIndicatorStyles';

export const NotificationBell = () => {
  const { translate } = useTranslation();
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light');
  const navigate = useNavigate();
  const notifications = useListNotifications({ pageSize: 10 });
  const setRead = useSetNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const unreadCount = notifications.data?.unreadCount ?? 0;
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [reportPreviewUrl, setReportPreviewUrl] = useState<string | null>(null);

  const openNotification = (notification: NotificationRecord) => {
    setPopoverOpened(false);
    if (!notification.readAt) {
      setRead.mutate({ notificationID: notification.id, read: true });
    }
    navigate(notification.actionUrl);
  };

  const toggleRead = (notification: NotificationRecord) => {
    setRead.mutate({
      notificationID: notification.id,
      read: !notification.readAt,
    });
  };

  const previewReport = (url: string) => {
    setPopoverOpened(false);
    setReportPreviewUrl(url);
  };

  return (
    <>
      <Popover
        width={380}
        position="bottom-end"
        shadow="md"
        opened={popoverOpened}
        onChange={setPopoverOpened}
      >
        <Popover.Target>
          <Tooltip label={translate('common:notificationCenter.title')}>
            <Indicator
              label={formatUnreadCount(unreadCount)}
              size={UNREAD_INDICATOR_SIZE}
              color={getUnreadIndicatorColor(theme, colorScheme)}
              disabled={unreadCount === 0}
            >
              <ActionIcon
                variant="subtle"
                size="lg"
                aria-label={translate('common:notificationCenter.title')}
                onClick={() => setPopoverOpened((opened) => !opened)}
              >
                <IconBell size={22} />
              </ActionIcon>
            </Indicator>
          </Tooltip>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={700}>
                {translate('common:notificationCenter.title')}
              </Text>
              <Button
                variant="subtle"
                size="compact-xs"
                disabled={unreadCount === 0}
                loading={markAllRead.isPending}
                onClick={() => markAllRead.mutate()}
              >
                {translate('common:notificationCenter.markAllRead')}
              </Button>
            </Group>
            {notifications.isLoading ? (
              <Box ta="center" py="md">
                <Loader size="sm" />
              </Box>
            ) : notifications.data?.data.length ? (
              <ScrollArea.Autosize mah={430}>
                <Stack gap="xs">
                  {notifications.data.data.map((notification) => (
                    <NotificationListItem
                      key={notification.id}
                      notification={notification}
                      compact
                      onOpen={openNotification}
                      onToggleRead={toggleRead}
                      onReportPreview={previewReport}
                    />
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            ) : (
              <Text ta="center" c="dimmed" py="md">
                {translate('common:notificationCenter.empty')}
              </Text>
            )}
            <Button
              variant="light"
              onClick={() => {
                setPopoverOpened(false);
                navigate('/notifications');
              }}
            >
              {translate('common:notificationCenter.viewAll')}
            </Button>
          </Stack>
        </Popover.Dropdown>
      </Popover>
      <ReportPreviewModal
        opened={Boolean(reportPreviewUrl)}
        previewUrl={reportPreviewUrl}
        onClose={() => setReportPreviewUrl(null)}
      />
    </>
  );
};
